<template>
  <div class="group relative z-0 w-full">
    <div class="relative">
      <input
        v-bind="dynamicIdAttr"
        class="input-default-2 peer"
        :placeholder="placeholder"
        :value="value"
        :type="type"
        :name="name"
        :max="max"
        :min="min"
        :step="step"
        :class="[
          {
            'text-danger-100': hasError,
            'border-none': !hasError,
            'has-value': hasValue,
            'has-placeholder': placeholder,
            'with-suffix': hasSuffix,
          },
          inputClasses,
        ]"
        :disabled="disabled"
        :readonly="readonly"
        @input="$emit('update:value', $event.target.value)"
      />

      <div class="suffix-container absolute bottom-[7px] right-3">
        <slot name="suffix" />
      </div>

      <span
        v-if="isLoading"
        class="absolute right-0 top-1/2 z-0 flex -translate-y-1/2 transform justify-center px-1"
      >
        <BaseLoader :active="isLoading" />
      </span>

      <slot />

      <label
        v-if="label"
        v-bind="dynamicForAttr"
        class="absolute left-[18px] top-[15px] font-light"
        :class="{
          'text-[#666666]': disabled || readonly,
          'text-black': !disabled && !readonly,
        }"
      >
        {{ label }}
        <span class="text-[#eb5757]" v-if="isRequired">*</span>
      </label>
    </div>

    <ErrorMessage v-if="hasError" class="mt-1">
      {{ $t(errorMsg) }}
    </ErrorMessage>

    <slot name="errors"></slot>
  </div>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import ErrorMessage from "@/components/Core/ErrorMessage.vue";
import BaseLoader from "@/components/Core/BaseLoader.vue";

export default {
  name: "BaseInput",
  components: { ErrorMessage, BaseLoader },
  emits: ["update:value"],
  props: {
    value: {
      type: [String, Number, null],
      required: false,
    },
    type: {
      type: String,
      default: "text",
    },
    id: {
      type: String,
      default: () => uuidv4(),
    },
    name: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    anyDirty: {
      type: Boolean,
      default: true,
    },
    errors: {
      type: Object,
      default: null,
    },
    inputClasses: {
      type: String,
      default: "",
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    max: {
      type: String,
    },
    min: {
      type: String,
    },
    step: {
      type: Number,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dynamicIdAttr() {
      return this.id ? { id: this.id } : {};
    },
    dynamicForAttr() {
      return this.id ? { for: this.id } : {};
    },
    hasValue() {
      return (
        this.value !== null && this.value !== undefined && this.value !== ""
      );
    },
    hasSuffix() {
      return !!this.$slots.suffix;
    },
    hasError() {
      return this.anyDirty && this.errors && this.errors?.[this.name];
    },
    errorMsg() {
      return this.errors[this.name] instanceof Array
        ? this.errors[this.name][0]
        : this.errors[this.name];
    },
  },
};
</script>

<style lang="scss" scoped>
input:focus-within:not(:disabled):not(:read-only) ~ label,
input.has-placeholder ~ label,
input.has-value ~ label,
input:is(:-webkit-autofill) ~ label {
  @apply -translate-y-2 transform text-[10px] uppercase tracking-widest text-[#666666];
}

input:focus-within,
input.has-placeholder,
input.has-value,
input:is(:-webkit-autofill) {
  @apply pb-[9px] pt-[21px];
}

.with-suffix {
  padding-right: 2rem;
}

.suffix-container {
  pointer-events: none;
}
</style>
